import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Bell, Search, Menu, ChevronDown } from "lucide-react";
import { BRAND } from "../lib/mockData";
import { auth } from "../lib/api";

// App-style dashboard shell with dark teal sidebar + topbar
export default function DashboardLayout({ role, roleLabel, nav, active, setActive, user, logoutTo = "/", children }) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeItem = nav.find((n) => n.id === active);

  const Sidebar = (
    <aside className="w-64 surface-deep text-white flex flex-col flex-shrink-0 h-full" data-testid="dashboard-sidebar">
      <div className="p-5 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#0E6E5E] to-[#0A3B36] flex items-center justify-center flex-shrink-0">
            <span className="font-display font-black text-xl text-[#7CC242]">U</span>
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-none">{BRAND.name}</div>
            <div className="text-[9px] tracking-[0.16em] uppercase text-[#7CC242] font-semibold mt-1">{roleLabel}</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {nav.map((item) => (
          <button
            key={item.id}
            onClick={() => { setActive(item.id); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-semibold transition ${
              active === item.id ? "bg-[#7CC242] text-[#0A2520]" : "text-white/70 hover:bg-white/5 hover:text-white"
            }`}
            data-testid={`sidebar-${item.id}`}
          >
            <item.icon size={18} />
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge != null && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${active === item.id ? "bg-[#0A2520] text-[#7CC242]" : "bg-[#7CC242] text-[#0A2520]"}`}>{item.badge}</span>
            )}
          </button>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-full object-cover"/>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <div className="text-xs text-white/50 truncate">{user.sub}</div>
          </div>
        </div>
        <button onClick={() => { auth.clear(); navigate(logoutTo); }} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-white/70 hover:bg-white/5 transition" data-testid="logout-btn">
          <LogOut size={16}/> Log out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex surface-cream overflow-hidden" data-testid={`dashboard-${role}`}>
      {/* Desktop sidebar */}
      <div className="hidden lg:block h-full">{Sidebar}</div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10 h-full">{Sidebar}</div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Topbar */}
        <header className="bg-white border-b border-[#E1E8E2] px-5 lg:px-8 py-4 flex items-center justify-between gap-4 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="dashboard-mobile-toggle"><Menu size={22}/></button>
            <div className="min-w-0">
              <h1 className="font-display font-bold text-xl text-[#0A2520] truncate">{activeItem?.label}</h1>
              <p className="text-xs text-[#5A6B66] hidden sm:block">{activeItem?.desc}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative hidden md:block">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6B66]"/>
              <input placeholder="Search…" className="input pl-9 py-2 w-52 text-sm"/>
            </div>
            <button className="relative w-10 h-10 rounded-full border border-[#E1E8E2] flex items-center justify-center hover:border-[#0E6E5E] transition" data-testid="notif-btn">
              <Bell size={17} className="text-[#3D4F4A]"/>
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#7CC242]"/>
            </button>
            <div className="flex items-center gap-2 pl-2">
              <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-full object-cover"/>
              <ChevronDown size={14} className="text-[#5A6B66] hidden sm:block"/>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-5 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
